/*
Telefon: intl-tel-input (flaga, lista kierunkowych, numer w E.164) z tymi samymi
ustawieniami co globalne utilities/inputTel.js, ale uruchamiany tutaj - globalny nie
przyjmuje opcji per pole, a landing jest tylko po polsku, więc lista krajów i wyszukiwarka
dostają polskie nazwy (i18n/pl). Pole ma `js-disable-intl-tel`, żeby globalny go pominął. Wymagamy numeru
komórkowego: kod idzie do jednej osoby, a backend (phone:INTERNATIONAL,PL) przepuściłby
też stacjonarny, np. "123123123" to dla biblioteki poprawny numer krakowski.

Gdy intl-tel-input albo jego utils (ładowane z CDN) nie wstaną, sprawdzamy numer
libphonenumber-js w wariancie max - min sprawdza tylko długość i nie zna typów numerów.
*/

import { phoneCountryOrder } from '@js/utilities/phoneCountries';

const MOBILE_TYPES = ['MOBILE', 'FIXED_LINE_OR_MOBILE'];

// Ta sama wersja utils co w utilities/inputTel.js.
const ITI_UTILS_URL = 'https://cdn.jsdelivr.net/npm/intl-tel-input@24.3.7/build/js/utils.js';

// Te same typy jako liczby - tak oddaje je intl-tel-input (enum numberType z libphonenumber).
const ITI_MOBILE_TYPES = [1, 2];

let lib = null;

export async function initPhoneInput(input) {
    if (input.intlTelInput) {
        return;
    }

    const [{ default: intlTelInput }, { default: pl }] = await Promise.all([
        import(/* webpackChunkName: "intl-tel-input" */ 'intl-tel-input'),
        import(/* webpackChunkName: "intl-tel-input" */ 'intl-tel-input/i18n/pl'),
        import('intl-tel-input/build/css/intlTelInput.css'),
    ]);

    // eslint-disable-next-line no-param-reassign
    input.intlTelInput = intlTelInput(input, {
        i18n: pl,
        initialCountry: 'pl',
        // Bez przykładowego numeru w polu - jak w koszyku (Phone.vue ma placeholder: '').
        autoPlaceholder: 'off',
        countryOrder: phoneCountryOrder,
        showSelectedDialCode: false,
        formatAsYouType: false,
        formatOnDisplay: false,
        utilsScript: ITI_UTILS_URL,
    });
}

export function loadPhoneLib() {
    lib ??= import(/* webpackChunkName: "libphonenumber-max" */ 'libphonenumber-js/max');

    return lib;
}

// Wynik z intl-tel-input albo null, gdy jego utils jeszcze/w ogóle nie ma.
async function readWithIntlTelInput(input) {
    const iti = input.intlTelInput;

    if (!iti) {
        return null;
    }

    await iti.promise?.catch(() => {});

    const valid = iti.isValidNumberPrecise();

    if (valid === null) {
        return null;
    }

    const mobile = ITI_MOBILE_TYPES.includes(iti.getNumberType());

    return { number: valid && mobile ? iti.getNumber() : null };
}

async function readWithLibphonenumber(value) {
    try {
        const { parsePhoneNumberFromString } = await loadPhoneLib();
        const parsed = parsePhoneNumberFromString(value, 'PL');

        return parsed?.isValid() && MOBILE_TYPES.includes(parsed.getType()) ? parsed.number : null;
    } catch (e) {
        lib = null;

        // Bez żadnej biblioteki oddajemy numer bez zmian - sprawdzi go backend.
        return value;
    }
}

// Numer komórkowy w E.164 albo null, gdy niepoprawny.
export async function readPhone(input) {
    const value = input.value.trim();

    if (!value) {
        return null;
    }

    const result = await readWithIntlTelInput(input);

    return result ? result.number : readWithLibphonenumber(value);
}
